import { api } from './http';
import { API_ENDPOINTS } from '@/config/constants';
import { logDownloadDiagnostics } from '@/utils/downloadDiagnostics';

// Types for single whitelist approach
export interface CurrentWhitelistFromApi {
  id: number;
  user_id: string;
  filename: string;
  size: number;
  content_type: string;
  file_url: string; // deprecated - will be removed
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  last_version_id: number; // ID of the latest version for download
}

export interface WhitelistVersionFromApi {
  id: number;
  filename: string;
  size: number;
  content_type: string;
  file_url: string; // deprecated - will be removed
  created_at: string;
  created_by: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

export interface WhitelistVersionsResponse {
  data: WhitelistVersionFromApi[];
}

// Get current active whitelist (there should be only one)
export async function getCurrentWhitelist(options: {
  signal?: AbortSignal;
} = {}): Promise<CurrentWhitelistFromApi | null> {
  try {
    const response = await api.get(API_ENDPOINTS.WHITELISTS, {
      signal: options.signal,
    });

    // If we have data and it's an array with items, take the first one
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data) &&
      response.data.data.length > 0
    ) {
      return response.data.data[0]; // Take the first (and should be only) whitelist
    }

    // No current whitelist
    return null;
  } catch (error: unknown) {
    // If 404, it means no whitelist exists yet
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
    }

    throw error;
  }
}

// Upload new whitelist (POST if no whitelist exists, PUT if one exists)
export async function uploadOrUpdateWhitelist(
  file: File,
  existingWhitelistId?: number
): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  if (existingWhitelistId) {
    // Update existing whitelist
    await api.put(`${API_ENDPOINTS.WHITELISTS}/${existingWhitelistId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return;
  }

  // Create new whitelist
  await api.post(API_ENDPOINTS.WHITELISTS, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

// Delete current whitelist (deletes all versions)
export async function deleteCurrentWhitelist(
  whitelistId: number
): Promise<void> {
  await api.delete(`${API_ENDPOINTS.WHITELISTS}/${whitelistId}`);
}

// Get all versions for a specific whitelist ID
export async function getWhitelistVersionsById(
  whitelistId: number
): Promise<WhitelistVersionsResponse> {
  const params = new URLSearchParams();
  params.append('whitelist_id', whitelistId.toString());

  const url = `${API_ENDPOINTS.WHITELISTS_VERSIONS}?${params.toString()}`;
  const response = await api.get(url);

  if (response.data && response.data.data && Array.isArray(response.data.data)) {
    return {
      data: response.data.data,
    };
  }

  return {
    data: [],
  };
}

// Alternative download method using window.open
async function downloadWhitelistFileFallback(
  fileUrl: string,
  _filename: string
): Promise<void> {
  const downloadWindow = window.open(fileUrl, '_blank');
  if (!downloadWindow) {
    throw new Error('Popup blocked - unable to download file');
  }
  setTimeout(() => {
    downloadWindow.close();
  }, 2000);
}

// Download whitelist file
export async function downloadWhitelistFile(
  fileUrl: string,
  filename: string
): Promise<void> {
  // Run diagnostics in development
  logDownloadDiagnostics(fileUrl, filename);

  // Check if fileUrl is absolute or relative
  let downloadUrl = fileUrl;
  if (!fileUrl.startsWith('http')) {
    if (fileUrl.startsWith('/')) {
      downloadUrl = fileUrl;
    } else {
      downloadUrl = `/api/${fileUrl}`;
    }
  }

  try {
    const response = await api.get(downloadUrl, {
      responseType: 'blob',
      timeout: 30000,
    });

    if (!response.data || response.data.size === 0) {
      await downloadWhitelistFileFallback(downloadUrl, filename);
      return;
    }

    const blob = new Blob([response.data]);
    const link = document.createElement('a');

    if ('download' in link) {
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } else {
      await downloadWhitelistFileFallback(downloadUrl, filename);
    }
  } catch {
    await downloadWhitelistFileFallback(downloadUrl, filename);
  }
}
